import * as Yup from 'yup';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import {
    DatePicker,
    Flex,
    Input,
    InputNumber,
    Select,
    Switch,
    Typography,
    Image,
} from 'antd';

import {
    FieldName,
    PROMOCODE_FIELD_NAMES,
    PROMOCODE_FORM_TYPE,
    PromocodeDTO,
    PromocodeFormType,
} from '../service/promocodes.interface';
import { TextError } from '../../../../../shared/ui/Typography/TextError';
import { ShopImageUpload } from '../../shops/components/ShopImageUpload';
import { Field } from '../../api-keys/utils/fields';
import { AiImageGenerator } from '../../../../ai/ui/AiImageGenerator';

const { Text } = Typography;

export const initialValues: PromocodeDTO = {
    is_active: true,
    code: '',
    discount: 10,
    quantity: 5,
    usage_limit: 20,
    disable_usage_limit: true,
    end_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
};

export const initialFilters = {
    page: 1,
    limit: 10,
    offset: 0,
};

export interface PromocodeField {
    name: FieldName;
    label: string;
    position: number;
    type: 'number' | 'text' | 'switch' | 'date';
    placeholder?: string;
    min?: number;
    max?: number;
    hidden?: (values: PromocodeDTO) => boolean;
}

export const getPromocodeFields = (
    formType: PromocodeFormType
): PromocodeField[] => {
    const baseFields: PromocodeField[] = [
        {
            name: PROMOCODE_FIELD_NAMES.CODE,
            label: 'Имя промокода',
            type: 'text',
            placeholder: 'Укажите текст промокода',
            position: 10,
        },
        {
            name: PROMOCODE_FIELD_NAMES.DISCOUNT,
            label: 'Размер скидки, %',
            type: 'number',
            min: 0,
            max: 100,
            position: 20,
        },
        {
            name: PROMOCODE_FIELD_NAMES.QUANTITY,
            label: 'Количество промокодов',
            type: 'number',
            min: 1,
            max: 10000,
            position: 25,
        },
        {
            name: PROMOCODE_FIELD_NAMES.END_DATE,
            label: 'Дата окончания',
            type: 'date',
            hidden: values => !values.is_active,
            position: 60,
        },
        {
            name: PROMOCODE_FIELD_NAMES.USAGE_LIMIT,
            label: 'Количество использований промокода',
            type: 'number',
            min: 1,
            hidden: values => !values.disable_usage_limit,
            position: 40,
        },
        {
            name: PROMOCODE_FIELD_NAMES.DISABLE_USAGE_LIMIT,
            label: 'Ограничить количество использований',
            type: 'switch',
            position: 30,
        },
        {
            name: PROMOCODE_FIELD_NAMES.IS_ACTIVE,
            label: 'Ограничить время действия',
            type: 'switch',
            position: 50,
        },
    ];

    if (formType === PROMOCODE_FORM_TYPE.GENERATED) {
        return baseFields.filter(
            field => field.name !== PROMOCODE_FIELD_NAMES.CODE
        );
    }

    if (formType === PROMOCODE_FORM_TYPE.UNIQUE) {
        return baseFields.filter(
            field => field.name !== PROMOCODE_FIELD_NAMES.QUANTITY
        );
    }

    return baseFields;
};

export const formTypes = [
    {
        label: 'Уникальный',
        value: PROMOCODE_FORM_TYPE.UNIQUE,
    },
    {
        label: 'Сгенерированный',
        value: PROMOCODE_FORM_TYPE.GENERATED,
    },
];

export const getValidationSchema = (formType: PromocodeFormType) =>
    Yup.object({
        code: Yup.string()
            .max(50)
            .when([], {
                is: () => formType === PROMOCODE_FORM_TYPE.UNIQUE,
                then: schema =>
                    schema.min(3, 'Минимальная длина 3 символа').required(),
                otherwise: schema => schema.notRequired(),
            }),

        discount: Yup.number().required().min(0).max(100),
        quantity: Yup.number().required().min(1).max(10000),
        disable_usage_limit: Yup.boolean(),

        usage_limit: Yup.string().when(
            PROMOCODE_FIELD_NAMES.DISABLE_USAGE_LIMIT,
            {
                is: true,
                then: schema => schema.required(),
            }
        ),

        end_date: Yup.string().required('Дата окончания обязательна'),
    });

export const renderField = (
    field: Field,
    formik: FormikProps<any>,
    opts?: {
        disabledFields?: string[];
        onBlur?: any;
    }
): JSX.Element | null => {
    const isDisabled = opts?.disabledFields?.includes(field.name);

    const components: Record<string, (field: PromocodeField) => JSX.Element> = {
        number: f => (
            <Flex gap={8} vertical key={f.name}>
                {f.label && <label>{f.label}</label>}
                <InputNumber
                    disabled={isDisabled}
                    name={f.name}
                    value={formik.values[f.name]}
                    min={f.min}
                    max={f.max}
                    onChange={val => formik.setFieldValue(f.name, val)}
                    onBlur={() => formik.validateField(f.name)}
                />
            </Flex>
        ),
        text: f => (
            <Flex gap={8} vertical key={f.name}>
                {f.label && <label>{f.label}</label>}
                <Input
                    disabled={isDisabled}
                    status={formik.errors[f.name] ? 'error' : ''}
                    name={f.name}
                    placeholder={f.placeholder}
                    value={formik.values[f.name]}
                    onChange={formik.handleChange}
                    onBlur={() => formik.validateField(f.name)}
                />
                {formik.errors[f.name] && (
                    <TextError text={formik.errors[f.name]} />
                )}
            </Flex>
        ),
        textarea: f => (
            <Flex gap={8} vertical key={f.name}>
                {f.label && <label>{f.label}</label>}
                <Input.TextArea
                    disabled={isDisabled}
                    style={{ height: 54, maxHeight: 254 }}
                    status={formik.errors[f.name] ? 'error' : ''}
                    name={f.name}
                    placeholder={f.placeholder}
                    value={formik.values[f.name]}
                    onChange={formik.handleChange}
                    onBlur={() => {
                        formik.validateField(f.name);
                        if (field.name === 'about' && opts.onBlur) {
                            opts.onBlur(formik.values[f.name]);
                        }
                    }}
                />
                {formik.errors[f.name] && (
                    <TextError text={formik.errors[f.name]} />
                )}
            </Flex>
        ),
        password: f => (
            <>
                <Flex gap={8} vertical key={f.name}>
                    {f.label && <label>{f.label}</label>}
                    <Input.Password
                        disabled={isDisabled}
                        status={formik.errors[f.name] ? 'error' : ''}
                        name={f.name}
                        placeholder={f.placeholder}
                        value={formik.values[f.name]}
                        onChange={formik.handleChange}
                        onBlur={() => formik.validateField(f.name)}
                    />
                </Flex>
                {formik.errors[f.name] && (
                    <TextError text={formik.errors[f.name]} />
                )}
            </>
        ),
        switch: f => (
            <Flex key={f.name} gap={8}>
                <Switch
                    disabled={isDisabled}
                    checked={formik.values[f.name]}
                    onChange={val => formik.setFieldValue(f.name, val)}
                />
                <Text>{f.label}</Text>
            </Flex>
        ),
        select: f => (
            <Flex vertical key={f.name} gap={8}>
                <Text>{f.label}</Text>
                <Select
                    disabled={isDisabled}
                    value={formik.values[f.name] ?? f.options?.[0]}
                    options={f.options}
                    onChange={val => formik.setFieldValue(f.name, val)}
                />
            </Flex>
        ),
        image: f => (
            <Flex vertical key={f.name} gap={8}>
                <Text>{f.label}</Text>
                {formik.values.photo_url ? (
                    <Image preview={false} src={formik.values.photo_url} />
                ) : (
                    <AiImageGenerator
                        targetParamName={'about'}
                        paramName={'photo_url'}
                        formik={formik}
                    >
                        <ShopImageUpload isEdit={true} formik={formik} />
                    </AiImageGenerator>
                )}
            </Flex>
        ),
        date: f => (
            <Flex vertical gap={8} key={f.name}>
                <label>{f.label}</label>
                <DatePicker
                    disabled={isDisabled}
                    allowClear={false}
                    style={{ width: '100%' }}
                    format='DD.MM.YYYY'
                    disabledDate={date =>
                        date &&
                        (date < dayjs().startOf('day') ||
                            date > dayjs('2050-12-31'))
                    }
                    value={
                        formik.values[f.name]
                            ? dayjs(formik.values[f.name])
                            : null
                    }
                    onChange={date => formik.setFieldValue(f.name, date)}
                />
            </Flex>
        ),
    };

    return components[field.type]?.(field) ?? null;
};
